import {produce} from 'immer';
import {v4 as uuidv4} from 'uuid';

import {ProteinCreationStructuredTable} from 'antibodies/nested-data-creation/full-screen/ProteinCreationStructuredTableAdapter';
import {
  ProteinCreationCellValue,
  ProteinCreationCellValueType,
  ProteinCreationRow,
} from 'antibodies/nested-data-creation/full-screen/ProteinCreationTypes';
import {StructuredTableColumn, StructuredTableRow} from 'notebook/structured-tables/StructuredTableConstants';
import {Cell} from 'notebook/tables/constants/CellConstants';
import {RowMetadata} from 'notebook/tables/constants/RowConstants';
import {TableColumnType} from 'notebook/tables/constants/TableColumnConstants';
import {BaseTypedValueType} from 'notebook/tables/typed-values/TypedValueConstants';
import {assertNever} from 'util/Types';

function isSchemaFieldColumn(column: StructuredTableColumn): boolean {
  return column.columnType === TableColumnType.TAG_VALUE && Boolean(column.schemaFieldApiId);
}

/** Convert a ProteinCreationCellValue to a structured table Cell. */
function nestedComponentValueToCell(value: ProteinCreationCellValue): Cell {
  switch (value.type) {
    case ProteinCreationCellValueType.EXISTING_ENTITY:
      return {
        text: '',
        typedValue: {
          type: BaseTypedValueType.BIOENTITY,
          bioentitySummary: {id: value.entityId, name: value.displayName},
        },
      };
    case ProteinCreationCellValueType.RAW_TEXT:
      return {text: value.text};
    case ProteinCreationCellValueType.BOOLEAN:
      return {
        text: '',
        typedValue: {type: BaseTypedValueType.BOOLEAN, booleanValue: value.value},
      };
    case ProteinCreationCellValueType.EMPTY:
      return {text: ''};
    default:
      assertNever(value);
      return {text: ''};
  }
}

/** Parse a structured table Cell into a ProteinCreationCellValue. */
function cellToNestedComponentValue(cell: Cell): ProteinCreationCellValue {
  if (cell.typedValue?.type === BaseTypedValueType.BIOENTITY) {
    return {
      type: ProteinCreationCellValueType.EXISTING_ENTITY,
      entityId: cell.typedValue.bioentitySummary.id,
      displayName: cell.typedValue.bioentitySummary.name,
    };
  }
  if (cell.typedValue?.type === BaseTypedValueType.BOOLEAN) {
    return {
      type: ProteinCreationCellValueType.BOOLEAN,
      value: cell.typedValue.booleanValue,
    };
  }
  if (cell.text !== '') {
    return {type: ProteinCreationCellValueType.RAW_TEXT, text: cell.text};
  }
  return {type: ProteinCreationCellValueType.EMPTY};
}

/** Create a blank row with empty values for every column in the table. */
export function createBlankRow(columns: ReadonlyArray<StructuredTableColumn>): ProteinCreationRow {
  const nestedComponents: Record<string, ProteinCreationCellValue> = {};
  const schemaFields: Record<string, ProteinCreationCellValue> = {};
  for (const column of columns) {
    if (isSchemaFieldColumn(column)) {
      schemaFields[column.schemaFieldApiId!] = {type: ProteinCreationCellValueType.EMPTY};
    } else {
      nestedComponents[column.name] = {type: ProteinCreationCellValueType.EMPTY};
    }
  }
  return {cid: uuidv4(), nestedComponents, schemaFields};
}

/**
 * Derive a StructuredTable from ProteinCreationRows.
 * Iterates columns to map ProteinCreationRow dicts to positional cells.
 */
export function proteinCreationRowsToStructuredTable(
  baseTable: ProteinCreationStructuredTable,
  rows: Array<ProteinCreationRow>
): ProteinCreationStructuredTable {
  const {columns} = baseTable;

  return produce(baseTable, (draft) => {
    draft.rows = rows.map((row) => {
      const cells: Array<Cell> = columns.map((column) => {
        const value = isSchemaFieldColumn(column)
          ? row.schemaFields[column.schemaFieldApiId!]
          : row.nestedComponents[column.name];
        return value ? nestedComponentValueToCell(value) : {text: ''};
      });
      return {
        isFinalized: false,
        rowId: row.cid,
        cells,
        metadata: {},
      };
    });
  });
}

/**
 * Parse a StructuredTable's rows back into ProteinCreationRows.
 * Iterates columns to map positional cells to ProteinCreationRow dicts.
 */
export function parseStructuredTableToRows(
  structuredTableRows: ReadonlyArray<StructuredTableRow<RowMetadata>>,
  baseColumns: ReadonlyArray<StructuredTableColumn>,
  existingRows: Array<ProteinCreationRow>
): Array<ProteinCreationRow> {
  const existingRowByCid = new Map(existingRows.map((row) => [row.cid, row]));
  return structuredTableRows.map((structuredRow) => {
    const existingRow = existingRowByCid.get(structuredRow.rowId);
    const nestedComponents: Record<string, ProteinCreationCellValue> = {};
    const schemaFields: Record<string, ProteinCreationCellValue> = {};
    baseColumns.forEach((column, i) => {
      const value = cellToNestedComponentValue(structuredRow.cells[i]);
      if (isSchemaFieldColumn(column)) {
        schemaFields[column.schemaFieldApiId!] = value;
      } else {
        nestedComponents[column.name] = value;
      }
    });
    return {
      cid: existingRow?.cid ?? uuidv4(),
      nestedComponents,
      schemaFields,
    };
  });
}
